<template>
  <div class="model-history">
    <el-card>
      <template #header>
        <div class="card-header">
          <el-icon><Histogram /></el-icon>
          <span>模型训练历史与性能监控</span>
          <el-button type="primary" @click="refreshData" :loading="loading" style="margin-left: auto;">
            <el-icon><Refresh /></el-icon> 刷新
          </el-button>
        </div>
      </template>

      <!-- 图表区域 -->
      <div ref="chartRef" style="width: 100%; height: 300px;"></div>

      <!-- 表格区域，使用 sortedHistoryList 进行降序展示 -->
      <el-table :data="sortedHistoryList" style="width: 100%; margin-top: 20px;" border>
        <el-table-column prop="timestamp" label="训练时间" width="180">
          <template #default="{ row }">{{ formatTime(row.timestamp) }}</template>
        </el-table-column>
        <el-table-column prop="val_acc" label="验证准确率(%)" width="120">
          <template #default="{ row }">{{ row.val_acc.toFixed(2) }}%</template>
        </el-table-column>
        <el-table-column prop="pre_val_acc" label="训练前准确率(%)" width="140">
          <template #default="{ row }">{{ row.pre_val_acc ? row.pre_val_acc.toFixed(2) + '%' : '-' }}</template>
        </el-table-column>
        <el-table-column prop="improvement" label="改进值(%)" width="100">
          <template #default="{ row }">
            <span :class="row.improvement > 0 ? 'improve' : row.improvement < 0 ? 'decline' : ''">
              {{ row.improvement ? (row.improvement > 0 ? '+' : '') + row.improvement.toFixed(2) + '%' : '-' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="correction_count" label="修正样本数" width="120" />
        <el-table-column label="操作" width="240">
          <template #default="{ row }">
            <el-button type="primary" link @click="downloadModel(row.filename)">下载</el-button>
            <el-button type="success" link @click="applyModel(row.filename)">应用</el-button>
            <el-button type="info" link @click="openAnalysis(row.filename)">分析</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 模型分析对话框 -->
      <model-analysis-dialog
          v-model:visible="analysisDialogVisible"
          :filename="selectedFilename"
      />
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue'
import { getModelHistory, downloadModelFile, applyModelVersion } from '@/api/captcha'
import * as echarts from 'echarts'
import { Histogram, Refresh } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import ModelAnalysisDialog from '@/components/ModelAnalysisDialog.vue'

const loading = ref(false)
const historyList = ref([])
const chartRef = ref(null)
let chart = null

// 分析对话框控制
const analysisDialogVisible = ref(false)
const selectedFilename = ref('')

// 计算属性：按时间戳降序排序（用于表格）
const sortedHistoryList = computed(() => {
  // 先复制数组，避免修改原数组
  return [...historyList.value].sort((a, b) => b.timestamp.localeCompare(a.timestamp))
})

const fetchHistory = async () => {
  loading.value = true
  try {
    const resp = await getModelHistory()
    if (resp.data.code === 200) {
      historyList.value = resp.data.data || []
      nextTick(() => {
        renderChart()
      })
    } else {
      ElMessage.error(resp.data.msg || '获取历史失败')
    }
  } catch (e) {
    ElMessage.error('请求失败')
  } finally {
    loading.value = false
  }
}

const renderChart = () => {
  if (!chartRef.value) return
  if (!chart) {
    chart = echarts.init(chartRef.value)
  }
  // 注意：图表仍然使用原始的 historyList 顺序（时间正序）
  const timestamps = historyList.value.map(item => formatTime(item.timestamp))
  const accData = historyList.value.map(item => item.val_acc)
  const preAccData = historyList.value.map(item => item.pre_val_acc || null)

  const option = {
    title: { text: '验证准确率变化趋势', left: 'center' },
    tooltip: { trigger: 'axis' },
    legend: {
      data: ['训练后准确率', '训练前准确率'],
      bottom: 10,
    },
    grid: {
      bottom: 150,
    },
    xAxis: {
      type: 'category',
      data: timestamps,
      axisLabel: { rotate: 60, margin: 5 }
    },
    yAxis: {
      type: 'value',
      min: 20,
      max: 100,
      name: '准确率(%)'
    },
    series: [
      { name: '训练后准确率', type: 'line', data: accData, smooth: true },
      { name: '训练前准确率', type: 'line', data: preAccData, smooth: true, lineStyle: { type: 'dashed' } }
    ]
  }
  chart.setOption(option)
  window.addEventListener('resize', () => chart?.resize())
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  const year = timestamp.slice(0, 4)
  const month = timestamp.slice(4, 6)
  const day = timestamp.slice(6, 8)
  const hour = timestamp.slice(9, 11)
  const minute = timestamp.slice(11, 13)
  const second = timestamp.slice(13, 15)
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`
}

const downloadModel = async (filename) => {
  try {
    const response = await downloadModelFile(filename)
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('下载失败')
  }
}

const applyModel = async (filename) => {
  try {
    await ElMessageBox.confirm(
        `确定要应用模型版本 ${filename} 吗？\n当前模型将被自动备份到历史目录，新模型将在1分钟内生效。`,
        '确认应用',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
    )
    const resp = await applyModelVersion(filename)
    if (resp.data.code === 200) {
      ElMessage.success(resp.data.msg || '应用成功')
    } else {
      ElMessage.error(resp.data.msg || '应用失败')
    }
  } catch (e) {
    if (e !== 'cancel') {
      console.error('应用模型失败', e)
      ElMessage.error('操作失败，请查看控制台日志')
    }
  }
}

// 打开分析对话框的函数
const openAnalysis = (filename) => {
  selectedFilename.value = filename
  analysisDialogVisible.value = true
}

const refreshData = () => {
  fetchHistory()
}

onMounted(() => {
  fetchHistory()
})
</script>

<style scoped>
.model-history {
  padding: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.improve {
  color: #67c23a;
  font-weight: bold;
}

.decline {
  color: #f56c6c;
  font-weight: bold;
}
</style>